import React from 'react';
import { toast } from 'react-toastify';

/** @type {import('react-toastify').ToastOptions} */
const defaultOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'colored',
};

const statusMessages = {
  initializing: 'Starting to generate your book preview...',
  savingTextElements: 'Saving your text elements...',
  generatingImagePrompts: 'Generating image prompts for your story...',
  generatingCharacter: 'Generating character images...',
  generatingCoverImage: 'Creating your book cover image...',
  updatingImageElements: 'Adding images to your book...',
  processing: 'Processing your book...',
  pending: 'Your book generation is starting...',
  pending2: 'Your book elements are being prepared...',
};

const withDefaults = (options = {}) => ({
  ...defaultOptions,
  ...options,
});

const withUpdateDefaults = (options = {}) => {
  const { position, ...baseOptions } = defaultOptions;
  return {
    ...baseOptions,
    ...options,
  };
};

const show = (method, message, options = {}) => {
  const toastId = options.toastId;

  if (toastId && toast.isActive(toastId)) {
    return toastId;
  }

  return method(message, withDefaults(options));
};

export const toastIds = {
  designStatus: (bookId) => `design-status-${bookId}`,
  worldImage: (type, uid) => `world-image-${type}-${uid}`,
  shipPdf: (bookId) => `ship-pdf-${bookId}`,
  printPdf: (bookId, pdfType) => `print-pdf-${bookId}-${pdfType}`,
  editStatus: (bookId) => `edit-status-${bookId}`,
};

export const notify = {
  success(message, options = {}) {
    return show(toast.success, message, options);
  },

  error(message, options = {}) {
    return show(toast.error, message, options);
  },

  info(message, options = {}) {
    return show(toast.info, message, options);
  },

  warning(message, options = {}) {
    return show(toast.warning, message, options);
  },

  loading(message, options = {}) {
    return show(toast.loading, message, {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      ...options,
    });
  },

  update(id, options = {}) {
    toast.update(id, withUpdateDefaults(options));
    return id;
  },

  dismiss(id) {
    toast.dismiss(id);
  },

  workflowStatus(workflowKey, status, options = {}) {
    if (!workflowKey || !status) return null;

    if (status === 'completed') {
      toast.dismiss(workflowKey);
      return null;
    }

    const message = options.message || statusMessages[status] || 'Processing your book...';

    if (toast.isActive(workflowKey)) {
      toast.update(workflowKey, withUpdateDefaults({
        render: message,
        type: options.type || 'info',
        autoClose: options.autoClose ?? 8000,
        ...options,
        toastId: workflowKey,
      }));
      return workflowKey;
    }

    return toast.info(message, withDefaults({
      autoClose: 8000,
      ...options,
      toastId: workflowKey,
    }));
  },

  processingTimeout(onCancel, options = {}) {
    const toastId = `processing-timeout-${Date.now()}`;
    
    const ToastContent = () => (
      <div className="flex items-center justify-between gap-4 bg-white border-l-4 border-amber-600 rounded p-4">
        <div className="flex items-center gap-3">
          <span className="text-xl">⚠️</span>
          <span className="text-gray-800 font-medium">Processing is taking longer than expected...</span>
        </div>
        <button
          onClick={() => {
            if (typeof onCancel === 'function') {
              onCancel();
            }
            toast.dismiss(toastId);
          }}
          className="px-4 py-2 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium text-sm rounded whitespace-nowrap transition-colors hover:bg-gray-50"
        >
          Cancel Process
        </button>
      </div>
    );

    return toast(<ToastContent />, withDefaults({
      autoClose: false,
      closeButton: true,
      ...options,
      toastId,
      type: 'default',
      style: { backgroundColor: 'transparent', padding: 0, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', width: '360px' },
    }));
  },
};

export default notify;
